import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@apollo/client";
import type { DateValueType } from "react-tailwindcss-datepicker";
import Autocomplete from "@/components/Autocomplete";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import ListItem from "@/components/ListItem/ListItem";
import Switch from "@/components/Switch";
import TextField from "@/components/TextField";
import { DEPARTMENTS_QUERY } from "@/features/global/components/AddTaskSideSheet/queries";
import {
  addWorkdayTeamTag,
  buildTeamVisibilityOptions
} from "@/features/global/components/AddTaskSideSheet/teamVisibilityOptions";
import TeamVisibilityField from "@/features/global/components/AddTaskSideSheet/TeamVisibilityField";
import {
  privateTaskSupportingText,
  hasDistinctPrivateAssignee
} from "@/features/global/components/AddTaskSideSheet/privateTaskCopy";
import { SOME_USERS_QUERY } from "@/pages/people/queries";
import {
  dateValueTypeToISOString,
  dateStringToDateValueType
} from "@/features/global/components/NotesTable/noteHelpers";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import useUpdateTask from "@/pages/tasks/_hooks/useUpdateTask";
import type { BackofficeCRMUpdateTaskFields } from "@/pages/tasks/_hooks/mutations";
import { TaskVisibilityValues, type CRMTask, type TaskVisibility } from "@/types";

type BackofficeUser = {
  id: string;
  name: string;
  email: string;
  department?: string | null;
};

interface EditTaskDialogProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  currentName: string;
  currentAssignee?: { id: string; name?: string | null; email?: string | null };
  currentDueDate?: string | null;
  currentVisibility?: TaskVisibility;
  task?: CRMTask;
}

function toBackofficeUser(assignee?: {
  id: string;
  name?: string | null;
  email?: string | null;
}): BackofficeUser | null {
  if (!assignee?.id) return null;
  return {
    id: assignee.id,
    name: assignee.name || assignee.email || assignee.id,
    email: assignee.email ?? ""
  };
}

function teamTagNames(task?: CRMTask): string[] {
  return (task?.teamTags ?? []).map((tag) => tag.name).filter(Boolean);
}

function sameTeamTags(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((name, index) => name === sortedB[index]);
}

export default function EditTaskDialog({
  open,
  onClose,
  taskId,
  currentName,
  currentAssignee,
  currentDueDate,
  currentVisibility = TaskVisibilityValues.PUBLIC,
  task
}: EditTaskDialogProps) {
  const currentUser = useCurrentBackofficeUser();

  const [name, setName] = useState(currentName);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState<BackofficeUser | null>(toBackofficeUser(currentAssignee));
  const [selectedTeamTags, setSelectedTeamTags] = useState<string[]>(() => teamTagNames(task));
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [dueDate, setDueDate] = useState<DateValueType>(() => {
    const parsed = dateStringToDateValueType(currentDueDate);
    return typeof parsed === "string" ? null : parsed;
  });
  const [isPrivate, setIsPrivate] = useState(currentVisibility === TaskVisibilityValues.PRIVATE);

  const teamTagsSignature = teamTagNames(task).join("|");

  const {
    update,
    loading: saving,
    error: mutationError
  } = useUpdateTask(taskId, {
    task,
    source: "task_detail"
  });

  const { data: usersData, loading: usersLoading } = useQuery(SOME_USERS_QUERY, {
    variables: { search: debouncedSearchTerm },
    skip: !debouncedSearchTerm
  });

  const { data: departmentsData } = useQuery(DEPARTMENTS_QUERY, {
    skip: !open
  });

  useEffect(() => {
    if (open) {
      setName(currentName);
      setSelectedAssignee(toBackofficeUser(currentAssignee));
      setSelectedTeamTags(teamTagNames(task));
      setTeamSearchTerm("");
      const parsed = dateStringToDateValueType(currentDueDate);
      setDueDate(typeof parsed === "string" ? null : parsed);
      setIsPrivate(currentVisibility === TaskVisibilityValues.PRIVATE);
      setSearchTerm("");
      setDebouncedSearchTerm("");
    }
  }, [open, currentName, currentAssignee, currentDueDate, currentVisibility, teamTagsSignature, task]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // The Autocomplete component's built-in constrainMenuWidth only fires on
  // mount, before the Dialog has laid out, so the anchor width is zero.
  // Re-apply width on the md-menu host element when search results arrive.
  const assigneeWrapperRef = useRef<HTMLDivElement>(null);
  const teamVisibilityWrapperRef = useRef<HTMLDivElement>(null);

  const syncMenuWidth = useCallback((wrapper: HTMLDivElement | null) => {
    if (!wrapper) return;
    const mdMenu = wrapper.querySelector("md-menu") as HTMLElement | null;
    const anchor = wrapper.querySelector(".relative");
    if (mdMenu && anchor) {
      const width = anchor.getBoundingClientRect().width;
      if (width > 0) {
        mdMenu.style.minWidth = `${width}px`;
        mdMenu.style.width = `${width}px`;
        mdMenu.style.maxWidth = `${width}px`;
      }
    }
  }, []);

  const syncAutocompleteMenus = useCallback(() => {
    syncMenuWidth(assigneeWrapperRef.current);
    syncMenuWidth(teamVisibilityWrapperRef.current);
  }, [syncMenuWidth]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(syncAutocompleteMenus);
  }, [open, usersData, departmentsData, teamSearchTerm, syncAutocompleteMenus]);

  function selectAssignee(user: BackofficeUser) {
    setSelectedAssignee(user);
    setSearchTerm(user.name);
    setSelectedTeamTags((prev) => addWorkdayTeamTag(prev, user.department));
  }

  function assignToMe() {
    if (!currentUser) return;
    selectAssignee({
      id: currentUser.id,
      name: currentUser.name,
      email: currentUser.email,
      department: currentUser.department
    });
  }

  function handleTeamVisibilitySelect(option: { value: string; label: string }) {
    setSelectedTeamTags((prev) => addWorkdayTeamTag(prev, option.value));
    setTeamSearchTerm("");
  }

  function removeTeamTag(department: string) {
    setSelectedTeamTags((prev) => prev.filter((tag) => tag !== department));
  }

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const dueDateValue = dateValueTypeToISOString(dueDate);
    const nameChanged = trimmedName !== currentName;
    const currentAssigneeId = currentAssignee?.id ?? null;
    const nextAssigneeId = selectedAssignee?.id ?? null;
    const assigneeChanged = nextAssigneeId !== currentAssigneeId;
    const dueDateChanged = dueDateValue !== (currentDueDate ?? null);
    const visibility = isPrivate ? TaskVisibilityValues.PRIVATE : TaskVisibilityValues.PUBLIC;
    const visibilityChanged = visibility !== currentVisibility;
    const currentTags = teamTagNames(task);
    const teamsChanged = !sameTeamTags(selectedTeamTags, currentTags);

    if (!nameChanged && !assigneeChanged && !dueDateChanged && !visibilityChanged && !teamsChanged) {
      onClose();
      return;
    }

    const fields: BackofficeCRMUpdateTaskFields = {};
    if (nameChanged) fields.name = trimmedName;
    if (assigneeChanged) fields.assignedToId = nextAssigneeId;
    if (dueDateChanged) fields.dueDate = dueDateValue;
    if (visibilityChanged) fields.visibility = visibility;
    if (teamsChanged) fields.teamTags = selectedTeamTags;

    const success = await update(fields);
    if (success) {
      onClose();
    }
  };

  const availableDepartmentOptions = buildTeamVisibilityOptions(
    departmentsData?.departments || [],
    selectedTeamTags,
    teamSearchTerm
  );

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Edit Task"
      className="w-[560px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="filled" onClick={handleSave} disabled={saving || !name.trim()} testId="save-edit-task">
            {saving ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {mutationError && (
          <Banner variant="error" description={mutationError} inline closable={false} hideSecondaryDismiss />
        )}

        <TextField
          label="Task Name"
          value={name}
          onChangeText={(v) => setName(v)}
          data-testid="edit-task-name"
          className="w-full"
        />

        <div ref={assigneeWrapperRef} className="space-y-2">
          <Autocomplete<BackofficeUser>
            label="Assignee"
            query={selectedAssignee ? selectedAssignee.name : searchTerm}
            loadingResults={usersLoading}
            data={usersData?.backofficeUsers || []}
            onChangeText={(value) => {
              if (selectedAssignee) {
                setSelectedAssignee(null);
              }
              setSearchTerm(value);
            }}
            reset={() => {
              setSelectedAssignee(null);
              setSearchTerm("");
              setDebouncedSearchTerm("");
            }}
            onSelect={selectAssignee}
            renderItem={(user: BackofficeUser) => (
              <ListItem compact useAvatar iconName="person" headline={user.name} supportingText={user.email} />
            )}
          />
          {currentUser && selectedAssignee?.id !== currentUser.id && (
            <Button variant="text" onClick={assignToMe} disabled={saving} testId="assign-to-me">
              Assign to Me
            </Button>
          )}
        </div>

        <TeamVisibilityField
          selectedTeamTags={selectedTeamTags}
          teamSearchTerm={teamSearchTerm}
          onTeamSearchChange={setTeamSearchTerm}
          availableDepartmentOptions={availableDepartmentOptions}
          onSelect={handleTeamVisibilitySelect}
          onRemove={removeTeamTag}
          data-testid="edit-team-visibility-autocomplete"
          autocompleteContainerRef={teamVisibilityWrapperRef}
        />

        <DateSelect
          label="Due Date"
          value={dueDate}
          onChange={(date) => setDueDate(typeof date === "string" ? null : date)}
          placeholder="Select a date"
          useSingleDate
          useModal
          popoverDirection="down"
          className="w-full"
        />

        <Switch
          disabled={saving}
          headline="Make Private"
          selected={isPrivate}
          supportingText={privateTaskSupportingText({
            hasDistinctAssignee: hasDistinctPrivateAssignee(selectedAssignee?.id, currentUser?.id),
            hasSelectedTeams: selectedTeamTags.length > 0
          })}
          onChange={() => setIsPrivate(!isPrivate)}
        />
      </div>
    </Dialog>
  );
}
